import React, { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectChecking, selectUser, setUser } from "./redux/Slice/authSlice";
import { startTracking, stopTracking, track } from "./utils/activityTracker";
import { errorMessage, setUnauthorizedHandler } from "./services";
import { getMe } from "./services/auth";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import AdminPanel from "./pages/AdminPanel";
import WebsiteEditor from "./pages/WebsiteEditor";
import ManageOrders from "./pages/ManageOrders";
import Toaster from "./components/Toaster";
import { AlertsProvider } from "./components/Alerts";
import { GlobalOrderAlarm } from "./components/NewOrderAlarm";
import ViewAsBanner from "./components/ViewAsBanner";
import { viewingAsBy } from "./utils/viewAs";
import "./pages/admin.css";
import Loader from "./components/Loader";
import OwnezzyLogo from "./components/OwnezzyLogo";

// Anything under /admin needs a signed-in user, and a user who must change a temporary password cannot skip that
function RequireAuth({ children }) {
  const user = useSelector(selectUser);
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // A Super Admin viewing as someone is never made to change that person's password
  if (user.mustChangePassword && !viewingAsBy()) return <ChangePassword />;
  return children;
}

function AppRoutes() {
  const user = useSelector(selectUser);
  const location = useLocation();

  // A new page (e.g. Manage orders from the websites list) starts at the top, not where the last page was scrolled to
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // The Super Admin's Admin Tracker: store owners' and staff's visits to the panel (utils/activityTracker.js)
  useEffect(() => {
    if (user) startTracking(user);
    else stopTracking();
  }, [user]);
  // Pages outside the tabs: the website editor and a website's orders
  useEffect(() => {
    const [, site, page] = location.pathname.match(/^\/admin\/sites\/([^/]+)\/(edit|orders)/) || [];
    if (site) track("page", page === "edit" ? "Website editor" : "Manage orders", site);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/admin" replace /> : <Login />} />
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route path="/admin" element={<RequireAuth><AdminPanel /></RequireAuth>} />
      <Route path="/admin/:tab" element={<RequireAuth><AdminPanel /></RequireAuth>} />
      <Route path="/admin/sites/:siteId/edit" element={<RequireAuth><WebsiteEditor key={location.pathname} /></RequireAuth>} />
      <Route path="/admin/sites/:siteId/orders" element={<RequireAuth><ManageOrders /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

export default function App() {
  const dispatch = useDispatch();
  const checking = useSelector(selectChecking);
  const [sessionError, setSessionError] = useState("");

  // When the API says the token is no longer valid, sign out and the guard sends the user to /login
  useEffect(() => {
    setUnauthorizedHandler(() => dispatch(logout()));
  }, [dispatch]);

  // A saved token is only a hint: ask the server who it belongs to before showing the panel
  const restoreSession = useCallback(() => {
    setSessionError("");
    getMe()
      .then(({ data }) => dispatch(setUser(data.user)))
      .catch((err) => {
        // a 401 already signed the user out; anything else (server down) lets them retry
        if (!err.response) setSessionError(errorMessage(err));
        else if (err.response.status !== 401) dispatch(logout());
      });
  }, [dispatch]);

  useEffect(() => {
    if (checking) restoreSession();
  }, [checking, restoreSession]);

  if (checking) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <div className="login-logo"><OwnezzyLogo wide /></div>
          {sessionError ? (
            <>
              <div className="form-error">{sessionError}</div>
              <button className="primary-btn" onClick={restoreSession}>Try again</button>
            </>
          ) : (
            <Loader label="Signing you in" />
          )}
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ViewAsBanner />
      {/* Alerts follow the user on every page (live, with sound and notifications); the bell shows them */}
      <AlertsProvider>
        <AppRoutes />
        {/* New orders ring on every page until they are accepted or rejected */}
        <GlobalOrderAlarm />
      </AlertsProvider>
      <Toaster />
    </BrowserRouter>
  );
}
